import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Loader2, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import MicButton from "@/components/MicButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AiWriteButton = ({ kind, value, onText, context = {}, testid = "ai-write" }) => {
  const { lang, t } = useLanguage();
  const { settings } = useSettings();
  const a = t.ai;
  const [open, setOpen] = useState(false);
  const [keywords, setKeywords] = useState("");
  const [loading, setLoading] = useState(false);
  if (settings.assistant?.write === false) return null;

  const openPanel = () => { setKeywords(value || ""); setOpen(true); };

  const generate = async (kw = keywords) => {
    if (kw.trim().length < 2) return toast.error(a.tooShort);
    setLoading(true);
    try {
      const r = await axios.post(`${API}/ai/write`, { kind, keywords: kw.trim(), lang, context });
      onText(r.data.text);
      setOpen(false);
      toast.success(a.done);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : a.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-2" data-testid={`${testid}-wrap`}>
      {!open ? (
        <button type="button" onClick={openPanel} data-testid={`${testid}-button`} className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-cream px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-gold transition-all hover:-translate-y-0.5 hover:bg-gold hover:text-white">
          <Sparkles className="h-3.5 w-3.5" /> {a.help}
        </button>
      ) : (
        <div className="rounded-2xl border border-gold/30 bg-cream p-4" data-testid={`${testid}-panel`}>
          <div className="flex items-start justify-between gap-3">
            <p className="text-xs leading-relaxed text-forest">{a.hint}</p>
            <button type="button" onClick={() => setOpen(false)} data-testid={`${testid}-close`} aria-label="Fermer" className="text-brand hover:text-gold"><X className="h-4 w-4" /></button>
          </div>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <input value={keywords} onChange={(e) => setKeywords(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); generate(); } }} placeholder={a.placeholder[kind] || a.placeholder.quote} data-testid={`${testid}-keywords`} className="w-full rounded-xl border border-brand/20 bg-white px-4 py-3 text-sm text-forest placeholder:text-brand/40 outline-none focus:border-gold" />
            <MicButton testid={`${testid}-mic`} onText={(text) => { setKeywords(text); generate(text); }} />
            <button type="button" onClick={() => generate()} disabled={loading} data-testid={`${testid}-generate`} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-gold px-5 py-3 font-mono text-[11px] uppercase tracking-[0.15em] text-white transition-all hover:bg-gold-hover disabled:opacity-60">
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {loading ? a.writing : a.generate}
            </button>
          </div>
          <p className="mt-2 text-[11px] text-brand-hover">{a.voiceHint}</p>
        </div>
      )}
    </div>
  );
};

export default AiWriteButton;
